import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageHeader, Card, Button, Spinner, Badge } from "@/components/ui";
import { endpoints, api } from "@/lib/api";
import { Send, Sparkles, ExternalLink, Globe, AtSign, Briefcase, Building2, Calculator, X, Paperclip, FileText, Gavel, Download, Bot } from "lucide-react";
import { pollJob, apiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const QUICK_PROMPTS = [
  "Value a SaaS company with €20M ARR and 40% growth",
  "What are the red flags in an SPA with an earn-out?",
  "How should I structure a buy-and-build in industrials?",
  "Run Deal Committee on Technoprobe",
];

const MODES = [
  { id: "", label: "Auto" },
  { id: "company_intelligence", label: "Company Intelligence" },
  { id: "document_analysis", label: "Document Analysis" },
  { id: "legal_advisor", label: "Legal Advisor" },
  { id: "financial_analyst", label: "Financial Analyst" },
];

function CommitteeCard({ c }) {
  const tone = { APPROACH: "green", WATCH: "gold", "RESEARCH MORE": "navy", PASS: "red" }[c.recommendation] || "neutral";
  const [dl, setDl] = useState(false);
  const pdf = async () => {
    setDl(true);
    try {
      const r = await api.get(`/committee/${c.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `CLARA_Committee_${c.company_name}.pdf`;
      a.click();
    } finally { setDl(false); }
  };
  return (
    <div className="ml-11 mt-2 p-4 rounded-md border border-[#C5A059]/40" style={{ background: "rgba(21,42,85,0.45)" }} data-testid="committee-card">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Gavel size={14} className="text-gold" />
        <span className="text-[11px] uppercase tracking-[0.16em] text-white/50">Virtual Deal Committee</span>
        <Badge tone={tone} data-testid="committee-recommendation">{c.recommendation}</Badge>
        <span className="font-mono text-gold text-[13px]" data-testid="committee-score">{c.committee_score}/100</span>
        <Badge tone="neutral">{c.confidence} confidence</Badge>
      </div>
      <Button size="sm" onClick={pdf} disabled={dl} data-testid="committee-pdf-btn"><Download size={12} /> {dl ? "Rendering…" : "Export PDF"}</Button>
    </div>
  );
}

const REF_ICONS = { deal: Briefcase, company: Building2, valuation: Calculator };

export default function AskClara() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [refs, setRefs] = useState([]); // active @-mention chips
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [sessionId] = useState(() => {
    const cached = sessionStorage.getItem("clara_chat_session");
    if (cached) return cached;
    const id = "clara_" + Math.random().toString(36).slice(2, 11);
    sessionStorage.setItem("clara_chat_session", id);
    return id;
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const c = params.get("committee");
    if (c) setInput(`Run Deal Committee on ${c}`);
  }, [params]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const r = await endpoints.chatUpload(sessionId, f);
      setFiles((x) => [...x, r.data]);
    } catch (ex) {
      alert("Upload failed: " + apiError(ex));
    } finally { setUploading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await endpoints.chatHistory(sessionId);
        setMessages(r.data);
      } catch {}
    })();
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // @-mention autocomplete: debounced
  useEffect(() => {
    if (!mentionOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/ai/chat/mentions?q=${encodeURIComponent(mentionQuery)}&limit=8`);
        setMentionResults(r.data || []);
      } catch {
        setMentionResults([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [mentionQuery, mentionOpen]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    // Detect trailing @token
    const match = v.match(/@(\S{0,40})$/);
    if (match) {
      setMentionOpen(true);
      setMentionQuery(match[1]);
    } else if (mentionOpen) {
      setMentionOpen(false);
    }
  };

  const pickMention = (m) => {
    // Strip the trailing @token from input, add chip
    const cleaned = input.replace(/@\S*$/, "").trimEnd();
    setInput(cleaned + (cleaned ? " " : ""));
    if (!refs.find((r) => r.type === m.type && r.id === m.id)) {
      setRefs([...refs, m]);
    }
    setMentionOpen(false);
    setMentionQuery("");
    inputRef.current?.focus();
  };

  const removeRef = (idx) => setRefs(refs.filter((_, i) => i !== idx));

  const send = async (text, extraRefs) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    const activeRefs = extraRefs || refs;
    setInput("");
    setMentionOpen(false);
    const optimistic = {
      id: "tmp-" + Date.now(),
      role: "user",
      content: msg,
      context_refs: activeRefs.length ? activeRefs.map(({ type, id, label }) => ({ type, id, label })) : undefined,
    };
    setMessages((m) => [...m, optimistic]);
    setSending(true);
    try {
      const r = await api.post("/ai/chat", {
        session_id: sessionId,
        message: msg,
        context_refs: activeRefs.length ? activeRefs.map(({ type, id }) => ({ type, id })) : undefined,
        mode: mode || undefined,
        file_ids: files.length ? files.map((f) => f.id) : undefined,
        async_mode: true,
      });
      setRefs([]);
      setFiles([]);
      const job = await pollJob(`/jobs/${r.data.job_id}`, null, 3000);
      if (job.status === "error") throw new Error(job.error || "Clara failed to answer");
      const res = job.result;
      setMessages((m) => [
        ...m,
        {
          id: "a-" + Date.now(),
          role: "assistant",
          content: res.answer,
          sources: res.sources || [],
          follow_ups: res.follow_ups || [],
          agents: res.agents || [],
          committee: res.committee || null,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { id: "e-" + Date.now(), role: "assistant", content: "Error: " + apiError(e) },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendFollowUp = (q) => send(q);

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 140px)" }}>
      <PageHeader
        eyebrow="Ask CLARA"
        title={<>Your private <span className="italic text-gold">M&A analyst</span>.</>}
        description="Six specialist agents — research, valuation, M&A, legal, due diligence and QC — reasoning over your workspace and uploaded files. Type “Run Deal Committee on [company]” for a full committee view."
        testid="ask-clara-page"
      />

      <Card className="flex-1 flex flex-col" hover={false}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 px-2 py-2 min-h-[40vh] max-h-[55vh]" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <Sparkles size={28} className="mx-auto text-gold mb-3" />
              <div className="text-white/60 mb-5 text-sm">Ask a question or pick a starter.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    data-testid={`prompt-${p.slice(0, 15).replace(/\s/g, '-')}`}
                    className="text-left text-[12px] p-3 clara-surface-2 rounded-md border border-white/5 hover:border-[#C5A059]/40 text-white/70 transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <div key={m.id} data-testid={`message-${m.role}`}>
              <div className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold ${
                  m.role === "user"
                    ? "bg-white/10 text-white"
                    : "gradient-gold text-black"
                }`}>
                  {m.role === "user" ? (user?.name?.[0]?.toUpperCase() || "U") : "C"}
                </div>
                <div className={`max-w-[82%] px-4 py-3 rounded-lg text-[13.5px] leading-relaxed ${
                  m.role === "user"
                    ? "bg-white/[0.04] border border-white/5 whitespace-pre-wrap"
                    : "clara-surface-2 border border-[#C5A059]/20 markdown-body"
                }`}>
                  {m.role === "assistant" ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  ) : (
                    <>
                      {m.context_refs?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {m.context_refs.map((r, i) => {
                            const Icon = REF_ICONS[r.type] || AtSign;
                            return (
                              <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#C5A059]/15 border border-[#C5A059]/40 text-gold text-[10.5px] font-medium">
                                <Icon size={10} /> {r.label || r.id?.slice(0, 8)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {m.content}
                    </>
                  )}
                </div>
              </div>

              {m.role === "assistant" && m.sources && m.sources.length > 0 && (
                <div className="ml-11 mt-2 space-y-1.5" data-testid="chat-sources-panel">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gold/80 font-medium">
                    <Globe size={11} /> Web Sources · Tavily
                  </div>
                  <div className="grid gap-1.5">
                    {m.sources.map((s, i) => (
                      <a
                        key={s.url + i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`source-${i + 1}`}
                        className="flex items-start gap-2 p-2 pr-3 rounded-md bg-white/[0.02] border border-white/5 hover:border-[#C5A059]/40 transition group"
                      >
                        <span className="text-[10px] font-mono text-gold shrink-0 mt-0.5">[{i + 1}]</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-white/85 truncate group-hover:text-white">
                            {s.title || s.url}
                          </div>
                          <div className="text-[10px] text-white/40 truncate">{s.url}</div>
                        </div>
                        <ExternalLink size={11} className="text-white/30 shrink-0 mt-1 group-hover:text-gold" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {m.role === "assistant" && m.agents?.length > 0 && (
                <div className="ml-11 mt-2 flex flex-wrap items-center gap-1" data-testid="chat-agents">
                  <Bot size={11} className="text-gold" />
                  {m.agents.map((a) => <Badge key={a} tone="navy" className="!text-[9px]">{a}</Badge>)}
                </div>
              )}

              {m.role === "assistant" && (m.committee || m.committee_id) && (
                <CommitteeCard c={m.committee || { id: m.committee_id, company_name: "committee", recommendation: "", committee_score: "—", confidence: "" }} />
              )}

              {m.role === "assistant" && m.follow_ups && m.follow_ups.length > 0 && (
                <div className="ml-11 mt-2 flex flex-wrap gap-1.5" data-testid="chat-follow-ups">
                  {m.follow_ups.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendFollowUp(q)}
                      data-testid={`follow-up-${i + 1}`}
                      className="text-[11.5px] px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/10 text-white/70 hover:border-[#C5A059]/50 hover:text-white transition"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full gradient-gold flex items-center justify-center text-[11px] font-semibold text-black">C</div>
              <div className="clara-surface-2 border border-[#C5A059]/20 px-4 py-3 rounded-lg flex items-center gap-2">
                <Spinner size={12} />
                <span className="text-[12px] text-white/50" data-testid="chat-thinking">CLARA's agents are working… (complex questions and Deal Committees can take 1–3 minutes)</span>
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 mt-4 border-t border-white/5">
          {/* Active reference chips */}
          {refs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="active-refs">
              {refs.map((r, i) => {
                const Icon = REF_ICONS[r.type] || AtSign;
                return (
                  <span key={`${r.type}-${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#C5A059]/15 border border-[#C5A059]/40 text-gold text-[11px]">
                    <Icon size={10} /> {r.label}
                    <button onClick={() => removeRef(i)} className="ml-1 text-gold/60 hover:text-gold" data-testid={`remove-ref-${i}`}>
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-modes">
            {MODES.map((md) => (
              <button key={md.id || "auto"} type="button" onClick={() => setMode(md.id)} data-testid={`chat-mode-${md.id || "auto"}`}
                className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${mode === md.id ? "border-[#C5A059] text-gold bg-[#C5A059]/10" : "border-white/10 text-white/50 hover:text-white"}`}>
                {md.label}
              </button>
            ))}
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-attached-files">
              {files.map((f, i) => (
                <span key={f.id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/[0.04] border border-white/10 text-white/80 text-[11px]">
                  <FileText size={10} className="text-gold" /> {f.filename}
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="ml-1 text-white/40 hover:text-white" data-testid={`remove-file-${i}`}><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 relative">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.xls,.csv,.tsv,.txt" className="hidden" onChange={onPickFile} data-testid="chat-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || sending} data-testid="chat-upload-btn" title="Attach PDF, Word, Excel or CSV">
              {uploading ? <Spinner size={13} /> : <Paperclip size={14} />}
            </Button>
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                className="w-full px-4 py-3 text-sm bg-white/[0.03] border border-white/10 rounded-md focus:outline-none focus:border-[#C5A059]/50 text-white placeholder:text-white/30"
                value={input}
                onChange={handleInputChange}
                placeholder="Ask CLARA anything… type @ to reference a deal, company or valuation"
                data-testid="chat-input"
                disabled={sending}
              />

              {mentionOpen && mentionResults.length > 0 && (
                <div
                  className="absolute bottom-full mb-2 left-0 right-0 max-h-64 overflow-y-auto rounded-md shadow-xl z-50"
                  style={{ background: "#0f0f13", border: "1px solid rgba(197,160,89,0.3)" }}
                  data-testid="mention-dropdown"
                >
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-white/40 border-b border-white/5 flex items-center gap-1.5">
                    <AtSign size={10} /> Reference an entity
                  </div>
                  {mentionResults.map((m) => {
                    const Icon = REF_ICONS[m.type] || AtSign;
                    return (
                      <button
                        key={`${m.type}-${m.id}`}
                        type="button"
                        onClick={() => pickMention(m)}
                        data-testid={`mention-option-${m.type}-${m.id.slice(0,8)}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#C5A059]/10 border-b border-white/5 last:border-0 flex items-center gap-2"
                      >
                        <Icon size={13} className="text-gold shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] text-white truncate">{m.label}</div>
                          <div className="text-[10px] text-white/40 truncate">{m.subtitle}</div>
                        </div>
                        <Badge tone="neutral" className="!text-[9px] !py-0 !px-1.5">{m.type}</Badge>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button type="submit" disabled={sending || !input.trim()} data-testid="chat-send-btn">
              <Send size={14} />
              <span className="hidden md:inline">Send</span>
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
